<template>
  <div v-if="gaps.length || error" class="kf-gaps neumorphism-flat">
    <div class="kf-gaps__head">
      <LucideIcon name="circle-alert" :size="18" class="kf-gaps__icon" />
      <span class="kf-gaps__title">Missing templates</span>
      <span class="kf-gaps__sub">
        borrowers no approved message fitted, last {{ days }} days
      </span>
      <v-spacer />
      <v-btn small text class="text-none" @click="open = !open">
        {{ open ? "Hide" : "Show " + gaps.length }}
      </v-btn>
    </div>

    <div v-if="error" class="kf-gaps__note">
      Couldn't load the missing templates.
      <v-btn small text color="primary" class="text-none" @click="load">Retry</v-btn>
    </div>

    <template v-else-if="open">
      <div v-for="gap in visible" :key="keyOf(gap)" class="kf-gap">
        <div class="kf-gap__main">
          <div class="kf-gap__info">
            <div class="kf-gap__head">
              <strong>{{ gap.type_label || gap.type || "Unknown type" }}</strong>
              <code v-if="gap.type" class="kf-code">{{ gap.type }}</code>
              <span class="kf-muted">· {{ gap.level_label }}</span>
              <span class="kf-gap__count">
                {{ gap.borrowers }} borrower{{ gap.borrowers === 1 ? "" : "s" }} · last
                {{ dayIst(gap.last_day) }}
              </span>
            </div>
            <TemplateGapNote :gap="gap" :show-type="false" />
            <div v-if="gap.worked_out_now" class="kf-gap__fine">
              Channels and rules worked out from these borrowers today.
            </div>
          </div>

          <!-- The actions sit on the right of each gap. -->
          <div class="kf-gap__actions">
            <v-btn
              small
              depressed
              color="primary"
              class="text-none"
              :disabled="!gap.needs.length"
              @click="addTemplate(gap.draft)"
            >
              <LucideIcon name="plus" :size="15" class="mr-1" />
              Add template
            </v-btn>
            <v-btn
              small
              outlined
              color="primary"
              class="text-none"
              :loading="isRunning(gap)"
              :disabled="!gap.needs.length"
              @click="suggest(gap)"
            >
              <LucideIcon name="bot" :size="15" class="mr-1" />
              Suggest with agent
            </v-btn>
          </div>
        </div>

        <div v-if="isRunning(gap)" class="kf-gap__fine">
          The agent is writing drafts; this can take two or three minutes.
        </div>
        <div v-else-if="stateOf(gap).error" class="kf-gap__fine kf-gap__error">
          {{ stateOf(gap).error }}
        </div>

        <!-- The agent's drafts, each with its own Add on the right. -->
        <div v-if="draftsOf(gap).length" class="kf-drafts">
          <div class="kf-drafts__head">
            <span v-if="stateOf(gap).from_agent">Agent's suggestions</span>
            <span v-else>The agent couldn't write drafts just now; add the template yourself.</span>
            <v-btn
              v-if="stateOf(gap).from_agent"
              x-small
              text
              color="primary"
              class="text-none"
              @click="suggest(gap, true)"
            >
              Ask again
            </v-btn>
          </div>
          <template v-if="stateOf(gap).from_agent">
            <div v-for="draft in draftsOf(gap)" :key="draft.name" class="kf-draft">
              <div class="kf-draft__text">
                <div class="kf-draft__meta">
                  <LucideIcon :name="channelIcon(draft.channel)" :size="14" />
                  {{ channelLabel(draft.channel) }} · {{ purposeLabel(draft.purpose) }}
                  <span v-if="draft.tone"> · {{ draft.tone }}</span>
                  <code class="kf-code">{{ draft.name }}</code>
                </div>
                <div v-if="draft.subject" class="kf-draft__subject">{{ draft.subject }}</div>
                <div class="kf-draft__body">{{ draft.body }}</div>
                <div v-if="draft.why" class="kf-gap__fine">{{ draft.why }}</div>
                <div v-if="draft.too_long" class="kf-gap__fine kf-gap__error">
                  Over 160 characters: shorten it before registering the SMS.
                </div>
              </div>
              <div class="kf-gap__actions">
                <v-btn small depressed color="primary" class="text-none" @click="addTemplate(draft)">
                  <LucideIcon name="plus" :size="15" class="mr-1" />
                  Add template
                </v-btn>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div v-if="gaps.length > visible.length" class="kf-gaps__note">
        <v-btn small text color="primary" class="text-none" @click="showAll = true">
          Show all {{ gaps.length }}
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * The Agent Tracker's "Missing templates": the borrowers the agent left off a
 * day because no approved message fitted them, grouped by type and level,
 * with what template would have fitted. "Suggest with agent" has the growth
 * agent draft the missing templates; "Add template" opens the Template
 * Library's "New template" form filled in with the gap (or one draft), for a
 * person to check and save; it is live once nothing it needs is missing (an
 * SMS DLT id, a Meta-approved WhatsApp name).
 */
import TemplateGapNote from "./TemplateGapNote.vue";
import api from "@/app-js/karrofin/api";
import { TEMPLATE_DRAFT_KEY, TEMPLATE_PURPOSES } from "@/app-js/karrofin/constants";
import { channelIcon, channelLabel, dayIst } from "@/pages/karrofin/agent/common";

/** Gaps shown before "Show all". */
const FIRST = 3;
/** How often a running draft is checked on, and for how long at most. */
const POLL_MS = 5000;
const POLL_FOR_MS = 5 * 60 * 1000;

export default {
  name: "KfAgentTemplateGaps",
  components: { TemplateGapNote },
  props: {
    /* Bumped by the page's Refresh, to load again. */
    reloadKey: { type: Number, default: 0 },
  },
  data() {
    return {
      // Hidden until asked for ("Show N"), so the borrower list comes first.
      gaps: [], days: 14, error: null, open: false, showAll: false,
      // {gapKey: {state, drafts, from_agent, error}}, filled once asked.
      suggestions: {},
      timers: {},
    };
  },
  computed: {
    visible() {
      return this.showAll ? this.gaps : this.gaps.slice(0, FIRST);
    },
  },
  watch: {
    reloadKey() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    Object.values(this.timers).forEach((t) => clearTimeout(t));
  },
  methods: {
    load() {
      this.error = null;
      api
        .getGrowthGaps()
        .then((data) => {
          this.gaps = data.results || [];
          this.days = (data.summary || {}).days || this.days;
        })
        .catch((err) => {
          this.error = err;
          this.gaps = [];
        });
    },
    keyOf(gap) {
      return gap.type + ":" + gap.level;
    },
    stateOf(gap) {
      return this.suggestions[this.keyOf(gap)] || {};
    },
    isRunning(gap) {
      return this.stateOf(gap).state === "running";
    },
    draftsOf(gap) {
      const s = this.stateOf(gap);
      return s.state === "done" ? s.drafts || [] : [];
    },
    setState(gap, state) {
      this.$set(this.suggestions, this.keyOf(gap), state);
    },
    suggest(gap, refresh) {
      const key = this.keyOf(gap);
      this.setState(gap, { state: "running" });
      api
        .suggestTemplates({ type: gap.type, level: gap.level, refresh: !!refresh })
        .then((reply) => {
          this.setState(gap, reply);
          if (reply.state === "running") this.poll(gap, Date.now());
        })
        .catch(() => {
          this.setState(gap, { state: "none", error: "Couldn't ask the agent. Try again." });
        });
      clearTimeout(this.timers[key]);
    },
    poll(gap, since) {
      const key = this.keyOf(gap);
      this.timers[key] = setTimeout(() => {
        api
          .getTemplateSuggestions({ type: gap.type, level: gap.level })
          .then((reply) => {
            if (reply.state === "running" && Date.now() - since < POLL_FOR_MS) {
              this.setState(gap, reply);
              this.poll(gap, since);
            } else if (reply.state === "running") {
              this.setState(gap, {
                state: "none",
                error: "The agent is taking too long. Try again in a few minutes.",
              });
            } else {
              this.setState(gap, reply);
            }
          })
          .catch(() => this.poll(gap, since));
      }, POLL_MS);
    },
    /** Hand the fields to the Template Library and open its New template form. */
    addTemplate(draft) {
      if (!draft) return;
      try {
        sessionStorage.setItem(TEMPLATE_DRAFT_KEY, JSON.stringify(draft));
      } catch (e) {
        return;
      }
      this.$router.push({ name: "karrofin-templates", query: { draft: "1" } });
    },
    purposeLabel(value) {
      const found = TEMPLATE_PURPOSES.find((p) => p.value === value);
      return found ? found.label : value;
    },
    channelIcon,
    channelLabel,
    dayIst,
  },
};
</script>

<style scoped>
.kf-gaps {
  margin: 12px 0;
  padding: 12px 16px;
  border-radius: 10px;
  border-left: 4px solid #e0a100;
  background: #fffaf0;
  font-size: var(--kf-fs-meta);
  color: #1f2d36;
}
.kf-gaps__head,
.kf-gap__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
}
.kf-gaps__icon {
  color: #b37f00;
}
.kf-gaps__title {
  font-size: var(--kf-fs-body);
  font-weight: 700;
}
.kf-gaps__sub,
.kf-gaps__note {
  color: #54636f;
}
.kf-gap {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f0e2bf;
}
.kf-gap__main,
.kf-draft {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}
.kf-gap__info,
.kf-draft__text {
  flex: 1 1 auto;
  min-width: 0;
}
.kf-gap__actions {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}
.kf-gap__count {
  margin-left: auto;
  color: #54636f;
  white-space: nowrap;
}
.kf-gap__fine {
  margin-top: 4px;
  font-size: var(--kf-fs-label);
  color: #54636f;
}
.kf-gap__error {
  color: #b3261e;
}
.kf-drafts {
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #f0e2bf;
}
.kf-drafts__head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #3f4d58;
}
.kf-draft {
  padding: 8px 0;
  border-top: 1px solid #f3ead4;
}
.kf-drafts__head + .kf-draft {
  border-top: 0;
}
.kf-draft__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
  color: #54636f;
}
.kf-draft__subject {
  margin-top: 4px;
  font-weight: 600;
}
.kf-draft__body {
  margin-top: 2px;
  white-space: pre-line;
  line-height: 1.4;
}
.kf-code {
  padding: 0 5px;
  border-radius: 4px;
  background: #eef3f8;
  color: #194e76;
  font-size: 0.86em;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  overflow-wrap: anywhere;
}
.kf-muted {
  color: #54636f;
}
@media (max-width: 599px) {
  .kf-gap__count {
    margin-left: 0;
  }
  .kf-gap__main,
  .kf-draft {
    flex-direction: column;
  }
  .kf-gap__actions {
    flex-direction: row;
    flex-wrap: wrap;
  }
}
</style>
